import { askConfirm, askText } from '../components/ask';
import { phoneField } from '../phone';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, get, post, put } from '../api';
import { toast } from '../components/toast';
import { ChecklistPicker } from '../components/ChecklistPicker';
import { ContactPicker } from '../components/ContactPicker';
import {
  ChecklistsIcon,
  ClockIcon,
  EditIcon,
  PeopleIcon,
  PersonIcon,
  ScheduleIcon,
} from '../components/NavIcons';
import { Row } from '../components/FormRow';
import { HistoryTable } from '../components/HistoryTable';
import { SkipDates } from '../components/SkipDates';
import { TagPicker } from '../components/TagPicker';
import { useBrand } from '../components/Brand';
import { contractorTypeOptions, type ContractorTypeOption } from '../contractorTypes';
import { dayLabel, todayLocal } from '../dates';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { assignmentRef } from '../numbers';
import { STATUS_LABELS, STATUS_TONE } from './AssignmentsList';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { AssignmentReview, type Preview } from '../components/AssignmentReview';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ORDINALS = ['1st', '2nd', '3rd', '4th', 'last'];
const UNITS: [string, string][] = [
  ['hour', 'Per hour'],
  ['visit', 'Per visit'],
  ['flat', 'Flat total'],
];
const UNIT_SHORT: Record<string, string> = { hour: '/h', visit: '/visit', flat: ' flat' };
const PATTERNS: [string, string][] = [
  ['weekly', 'Every week'],
  ['fortnightly', 'Every 2 weeks'],
  ['triweekly', 'Every 3 weeks'],
  ['monthly', 'Monthly'],
  ['manual', 'Manual — pick each date'],
];

interface Slot {
  weekday: number;
  ordinal: string;
  week: number;
  /** The one calendar date this visit is for, on the manual pattern only. */
  date: string;
  start_time: string;
  end_time: string;
}
interface Member {
  contact_id: number;
  pay_rate: string;
  pay_rate_unit: string;
}
/** The customer's own details: shown on the assignment for review, saved to the customer. */
interface CustomerDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: { line1: string; locality: string; postcode: string };
  access_notes: string;
}
export interface Form {
  customer_id: number;
  client_id: number;
  referrer_id: number;
  client_reference: string;
  charge_rate: string;
  charge_rate_unit: string;
  job_types: string[];
  lead_id: number;
  lead_pay_rate: string;
  lead_pay_rate_unit: string;
  team: Member[];
  open_to_claim: boolean;
  offer_pay_rate: string;
  offer_pay_rate_unit: string;
  own_address: boolean;
  service_address: {
    line1: string;
    line2: string;
    locality: string;
    region: string;
    postcode: string;
    country: string;
  };
  own_access_notes: boolean;
  access_notes: string;
  start_date: string;
  end_date: string;
  visit_count: number;
  invoice_description: string;
  notes: string;
  pattern: string;
  slots: Slot[];
  effective_date: string;
  tag_ids: number[];
  checklist_template_id: number;
  customer_details: CustomerDetails;
}

interface Loaded extends Partial<Form> {
  id: number;
  number?: number;
  customer_name: string;
  client_name: string;
  lead_name: string;
  customer: CustomerDetails | null;
  future_visits: number;
  assignment_status: string;
  status_note: string | null;
  next_statuses: { status: string; label: string; needs_note: boolean }[];
}

const NO_CUSTOMER: CustomerDetails = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: { line1: '', locality: '', postcode: '' },
  access_notes: '',
};

const BLANK: Form = {
  customer_id: 0,
  client_id: 0,
  referrer_id: 0,
  client_reference: '',
  charge_rate: '',
  charge_rate_unit: 'hour',
  job_types: [],
  lead_id: 0,
  lead_pay_rate: '',
  lead_pay_rate_unit: 'hour',
  team: [],
  open_to_claim: false,
  offer_pay_rate: '',
  offer_pay_rate_unit: 'hour',
  own_address: false,
  service_address: { line1: '', line2: '', locality: '', region: '', postcode: '', country: '' },
  own_access_notes: false,
  access_notes: '',
  start_date: '',
  end_date: '',
  visit_count: 0,
  invoice_description: '',
  notes: '',
  pattern: 'weekly',
  slots: [{ weekday: 1, ordinal: '', week: 1, date: '', start_time: '09:00', end_time: '11:00' }],
  effective_date: '',
  tag_ids: [],
  checklist_template_id: 0,
  customer_details: NO_CUSTOMER,
};

/** A status move as the office asks for it: a verb, not the state it lands in. */
const MOVE_LABEL: Record<string, string> = {
  scheduled: 'Schedule it',
  draft: 'Back to draft',
  on_hold: 'Put on hold…',
  completed: 'Mark completed…',
  cancelled: 'Cancel assignment…',
};

/**
 * The form's tabs. A new job steps through them in order; an existing one opens on Team & rates,
 * and has its audit history as a last tab.
 */
type Tab = 'customer' | 'team' | 'schedule' | 'extras' | 'history';
const TABS: Record<Tab, { label: string; short: string; Icon: () => ReactNode }> = {
  customer: { label: 'Customer & billing', short: 'Customer', Icon: PersonIcon },
  team: { label: 'Team & rates', short: 'Team', Icon: PeopleIcon },
  schedule: { label: 'Schedule', short: 'Schedule', Icon: ScheduleIcon },
  extras: { label: 'Extras', short: 'Extras', Icon: ChecklistsIcon },
  history: { label: 'Audit history', short: 'History', Icon: ClockIcon },
};

/** Which tab shows the field a server error names; null for those above the tabs. */
function tabOf(field: string): Tab | null {
  if (/^(new_)?customer_|^service_address_|^(client|referrer)(_contact)?_id$/.test(field))
    return 'customer';
  if (/^(client_reference|invoice_)/.test(field)) return 'customer';
  if (/^(charge_|lead_|offer_|open_to_claim|team|job_types)/.test(field)) return 'team';
  if (/^(pattern|start_date|end_date|visit_count|slots)/.test(field)) return 'schedule';
  if (/^(checklist_template_id|tag_ids|notes)$/.test(field)) return 'extras';
  return null;
}

/** A plain labelled input, for values that have no picker. */
function Field(props: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className={props.error ? 'field invalid' : 'field'}>
      <label>
        {props.label}
        {props.children}
      </label>
      {props.error && <span className="error">{props.error}</span>}
    </div>
  );
}

const minutesOf = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const cents = (x: string) => Math.round(Number(x || 0) * 100);

/** What one visit is worth at a rate, in cents: by the hour, by the visit, or a share of a flat total. */
function perVisit(rate: string, unit: string, minutes: number, visits: number) {
  if (unit === 'hour') return Math.round((cents(rate) * minutes) / 60);
  if (unit === 'flat') return Math.round(cents(rate) / Math.max(visits, 1));
  return cents(rate);
}

/** Margin on a typical visit, from what is on screen, for the Rates section to show as it's typed. */
function marginOf(f: Form) {
  const lengths = f.slots
    .map((s) => minutesOf(s.end_time) - minutesOf(s.start_time))
    .filter((m) => m > 0);
  const minutes = lengths.length ? lengths.reduce((a, b) => a + b, 0) / lengths.length : 0;
  const charge = perVisit(f.charge_rate, f.charge_rate_unit, minutes, f.visit_count);
  const lead =
    f.lead_id > 0
      ? perVisit(f.lead_pay_rate, f.lead_pay_rate_unit, minutes, f.visit_count)
      : f.open_to_claim
        ? perVisit(f.offer_pay_rate, f.offer_pay_rate_unit, minutes, f.visit_count)
        : 0;
  const team = f.team.reduce(
    (s, m) => s + perVisit(m.pay_rate, m.pay_rate_unit, minutes, f.visit_count),
    0,
  );
  const pay = lead + team;
  return { charge, pay, margin: charge - pay, known: charge > 0 && minutes > 0 };
}

const rateText = (rate: string, unit: string) =>
  rate ? `${formatMoney(rate)}${UNIT_SHORT[unit] ?? ''}` : 'none';

function patternText(f: Pick<Form, 'pattern' | 'slots'>) {
  const name = PATTERNS.find(([v]) => v === f.pattern)?.[1] ?? f.pattern;
  if (f.pattern === 'manual') return `${name} (${f.slots.length} dates)`;
  const days = f.slots
    .map((s) => `${DAYS[s.weekday - 1]?.slice(0, 3)} ${s.start_time}–${s.end_time}`)
    .join(', ');
  return `${name}, ${days}`;
}

/** What an edit changes, in words, for the review before saving. */
function changesOf(before: Form, after: Form, leadNames: { before: string; after: string }) {
  const out: string[] = [];
  const diff = (what: string, a: string, b: string) => a !== b && out.push(`${what}: ${a} → ${b}`);
  if (before.lead_id !== after.lead_id)
    out.push(`Lead: ${leadNames.before || 'nobody'} → ${leadNames.after || 'nobody'}`);
  diff(
    'Charge',
    rateText(before.charge_rate, before.charge_rate_unit),
    rateText(after.charge_rate, after.charge_rate_unit),
  );
  if (after.lead_id > 0)
    diff(
      'Lead pay',
      rateText(before.lead_pay_rate, before.lead_pay_rate_unit),
      rateText(after.lead_pay_rate, after.lead_pay_rate_unit),
    );
  if (after.open_to_claim || before.open_to_claim)
    diff(
      'Offered pay',
      before.open_to_claim
        ? rateText(before.offer_pay_rate, before.offer_pay_rate_unit)
        : 'not offered',
      after.open_to_claim
        ? rateText(after.offer_pay_rate, after.offer_pay_rate_unit)
        : 'not offered',
    );
  if (JSON.stringify(before.team) !== JSON.stringify(after.team))
    out.push(
      `Team: ${before.team.length} → ${after.team.length} ${after.team.length === 1 ? 'member' : 'members'} (or their pay)`,
    );
  diff('Schedule', patternText(before), patternText(after));
  diff(
    'Ends',
    before.end_date ? dayLabel(before.end_date) : 'no end date',
    after.end_date ? dayLabel(after.end_date) : 'no end date',
  );
  if (before.client_id !== after.client_id) out.push('Billed to: a different client');
  diff('Claim number', before.client_reference || 'none', after.client_reference || 'none');
  if (JSON.stringify(before.customer_details) !== JSON.stringify(after.customer_details))
    out.push("Customer's details (saved to their record)");
  if (
    before.own_address !== after.own_address ||
    JSON.stringify(before.service_address) !== JSON.stringify(after.service_address) ||
    before.own_access_notes !== after.own_access_notes ||
    before.access_notes !== after.access_notes
  )
    out.push('Address or access notes for this job');
  if (before.checklist_template_id !== after.checklist_template_id) out.push('Checklist');
  if (JSON.stringify(before.tag_ids) !== JSON.stringify(after.tag_ids)) out.push('Tags');
  if (before.invoice_description !== after.invoice_description) out.push('Invoice description');
  if (before.notes !== after.notes) out.push('Office notes');
  return out;
}

/**
 * A new assignment filled in from another: its customer, billing, team, rates, schedule and
 * extras, but no dates, so the office picks when the copy starts. Nothing of the original's
 * status or visits comes with it; the copy is saved, and its visits made, only as any new one is.
 */
function copyOf(from: Loaded): Form {
  const out = { ...BLANK };
  for (const k of Object.keys(BLANK) as (keyof Form)[]) {
    if (from[k] !== undefined && from[k] !== null) (out as Record<string, unknown>)[k] = from[k];
  }
  return {
    ...out,
    customer_details: from.customer ?? NO_CUSTOMER,
    start_date: '',
    end_date: '',
    effective_date: '',
  };
}

export function AssignmentForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  // "Duplicate" opens a new assignment from this one.
  const from = id ? null : params.get('from');
  const [attempt, setAttempt] = useState(0);
  const existing = useLoad(
    () => {
      const source = id ?? from;
      return source ? get<Loaded>(`/api/assignments/${source}`) : Promise.resolve(null);
    },
    [id, from, attempt],
  );
  if ((id || from) && existing.loading && !existing.data)
    return (
      <div className="stack form-skeleton" aria-busy="true">
        <p className="sr-only">Loading…</p>
        <div className="skeleton h1" />
        <div className="skeleton panel-block" />
        <div className="skeleton panel-block" />
      </div>
    );
  if ((id || from) && existing.error) {
    return (
      <div className="stack narrow">
        <p role="alert" className="error">
          Could not load this assignment: {existing.error}
        </p>
        <div className="row start">
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
          <Link to="/assignments">Back to assignments</Link>
        </div>
      </div>
    );
  }
  if (from && existing.data) {
    const source = existing.data;
    return (
      <Editor
        key={`copy-${from}`}
        initial={copyOf(source)}
        loaded={null}
        ended={false}
        copy={{ name: source.customer_name, person: !!source.customer }}
      />
    );
  }
  const loaded = existing.data;
  const live = loaded && loaded.assignment_status !== 'draft';
  const initial: Form = loaded
    ? {
        ...BLANK,
        ...loaded,
        customer_details: loaded.customer ?? NO_CUSTOMER,
        // An edit applies from a date; today is what the office means nine times in ten.
        effective_date: live ? todayLocal() : '',
      }
    : BLANK;
  const ended =
    loaded?.assignment_status === 'scheduled' &&
    !!loaded.end_date &&
    loaded.end_date < todayLocal();
  return <Editor key={id ?? 'new'} id={id} initial={initial} loaded={loaded} ended={ended} />;
}

/** Per hour / per visit / flat total, as one radio group: arrow keys move, Tab leaves. */
function Units(props: { name: string; value: string; onChange(v: string): void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const at = Math.max(
    0,
    UNITS.findIndex(([v]) => v === props.value),
  );
  const key = (e: KeyboardEvent) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (at + step + UNITS.length) % UNITS.length;
    props.onChange(UNITS[next]![0]);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={props.name} className="seg" onKeyDown={key}>
      {UNITS.map(([v, l], i) => (
        <button
          key={v}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={props.value === v}
          tabIndex={i === at ? 0 : -1}
          className={props.value === v ? 'on' : ''}
          onClick={() => props.onChange(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** A rate: how it's counted, and the amount. */
function Rate(props: {
  name: string;
  /** What the per hour / per visit / flat choice is called, e.g. "Charge type". */
  unitLabel: string;
  unit: string;
  value: string;
  disabled?: boolean;
  onUnit(v: string): void;
  onValue(v: string): void;
}) {
  return (
    <div className="inline">
      <Units name={props.unitLabel} value={props.unit} onChange={props.onUnit} />
      <label className="inline-label">
        $
        <input
          inputMode="decimal"
          aria-label={props.name}
          className="short num"
          disabled={props.disabled}
          value={props.value}
          onChange={(e) => props.onValue(e.target.value)}
        />
      </label>
    </div>
  );
}

/**
 * The types of work an assignment needs: a dropdown to add one, and each added type
 * as a chip that can be removed. None added means any type of work.
 */
function JobTypes(props: {
  options: ContractorTypeOption[];
  value: string[];
  onChange(v: string[]): void;
}) {
  const label = (v: string) => props.options.find((t) => t.value === v)?.label ?? v;
  const left = props.options.filter((t) => !props.value.includes(t.value));
  const selectRef = useRef<HTMLSelectElement>(null);
  return (
    <div className="job-types">
      {/* What is chosen comes first, so the dropdown never sits over it. */}
      {props.value.length === 0 ? (
        <p className="muted">Any type of work</p>
      ) : (
        <ul className="chips" aria-label="Types of work">
          {props.value.map((v) => (
            <li key={v} className="chip">
              {label(v)}
              <button
                type="button"
                className="quiet"
                onClick={() => {
                  props.onChange(props.value.filter((x) => x !== v));
                  // The chip and its button are gone; carry on from the dropdown, once it is
                  // enabled again (it was off while every type was chosen).
                  requestAnimationFrame(() => selectRef.current?.focus());
                }}
              >
                ×<span className="sr-only"> Remove {label(v)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <select
        ref={selectRef}
        aria-label="Add a type of work"
        value=""
        disabled={left.length === 0}
        onChange={(e) => e.target.value && props.onChange([...props.value, e.target.value])}
      >
        <option value="">{left.length === 0 ? 'All types added' : 'Add a type of work…'}</option>
        {left.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Editor(props: {
  id?: string;
  initial: Form;
  loaded: Loaded | null;
  ended: boolean;
  /** Set when this new assignment is a copy: whose it was, and whether that customer is a person. */
  copy?: { name: string; person: boolean };
}) {
  const nav = useNavigate();
  const brand = useBrand();
  const jobTypes = contractorTypeOptions(brand?.slug ?? '');
  const [f, setF] = useState<Form>(props.initial);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const problemsRef = useRef<HTMLDivElement>(null);
  const editing = props.id !== undefined;
  const isDraft = props.loaded?.assignment_status === 'draft';
  const live = editing && !isDraft;
  const [customerMode, setCustomerMode] = useState<'new' | 'existing'>(
    props.initial.customer_id > 0 ? 'existing' : 'new',
  );
  // An organisation as customer has no person details to show; it's edited on its own page.
  // No details loaded means none to show, so none are sent back to overwrite the customer's.
  const [personCustomer, setPersonCustomer] = useState(
    props.copy ? props.copy.person : !editing || !!props.loaded?.customer,
  );
  const [picking, setPicking] = useState(false);
  // What saving would create, shown for confirmation before anything is saved.
  const [review, setReview] = useState<Preview | null>(null);
  // Office notes edited where they are shown, at the top, and saved there and then on Done.
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);
  const [notesError, setNotesError] = useState('');
  // Notes saved that way are saved: the rest of the form compares against them, not the load.
  const [savedNotes, setSavedNotes] = useState(props.initial.notes);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const notesEditRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (editingNotes) notesRef.current?.focus();
  }, [editingNotes]);
  // A new job (or a draft) is filled in step by step; a live one leads with what changes most.
  const stepped = !live;
  const steps: Tab[] = live
    ? ['team', 'schedule', 'customer', 'extras']
    : ['customer', 'team', 'schedule', 'extras'];
  // History only exists once the assignment does; it is never one of the steps.
  const order: Tab[] = editing ? [...steps, 'history'] : steps;
  const [tab, setTab] = useState<Tab>(order[0]!);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((s) => ({ ...s, [k]: v }));
  const setCust = (patch: Partial<CustomerDetails>) =>
    setF((s) => ({ ...s, customer_details: { ...s.customer_details, ...patch } }));
  const setSlot = (i: number, patch: Partial<Slot>) =>
    set(
      'slots',
      f.slots.map((s, n) => (n === i ? { ...s, ...patch } : s)),
    );
  const setMember = (i: number, patch: Partial<Member>) =>
    set(
      'team',
      f.team.map((m, n) => (n === i ? { ...m, ...patch } : m)),
    );
  const fail = (err: unknown) => {
    if (err instanceof ApiError) {
      setProblems(err.problems.length ? err.problems : [err.message]);
      setFields(err.fields);
      // Open the first tab with a problem on it, so the office sees the field, not just the list.
      const flagged = order.find((t) => Object.keys(err.fields).some((k) => tabOf(k) === t));
      if (flagged) setTab(flagged);
    } else setProblems(['Could not reach the server']);
    // The summary is at the top; bring it into view so the office sees what went wrong.
    requestAnimationFrame(() => problemsRef.current?.focus());
  };

  /** Pick a different customer: their details replace what's shown, for review. */
  async function pickCustomer(id: number) {
    set('customer_id', id);
    if (id <= 0) return;
    try {
      const c = await get<{
        kind: string;
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
        mobile: string;
        address: { line1: string; locality: string; postcode: string } | null;
        customer: { access_notes: string | null } | null;
      }>(`/api/contacts/${id}`);
      setPersonCustomer(c.kind !== 'organisation');
      setCust({
        first_name: c.first_name ?? '',
        last_name: c.last_name ?? '',
        email: c.email ?? '',
        phone: c.mobile || c.phone || '',
        address: {
          line1: c.address?.line1 ?? '',
          locality: c.address?.locality ?? '',
          postcode: c.address?.postcode ?? '',
        },
        access_notes: c.customer?.access_notes ?? '',
      });
      setPicking(false);
    } catch (err) {
      fail(err);
    }
  }

  const payloadOf = () => {
    const { customer_details: cd, ...rest } = f;
    const payload: Record<string, unknown> = { ...rest };
    if (customerMode === 'new') {
      // A new customer is made from these details, and the job is at their address.
      payload.new_customer = {
        first_name: cd.first_name,
        last_name: cd.last_name,
        email: cd.email,
        phone: cd.phone,
      };
      payload.service_address = { ...BLANK.service_address, ...cd.address };
      payload.access_notes = cd.access_notes;
      payload.own_address = false;
      payload.own_access_notes = false;
    } else if (personCustomer && f.customer_id > 0) {
      payload.customer_details = cd;
    }
    // Said outright, so the review checks "changes apply from" just as saving does.
    if (live) payload.editing = true;
    return payload;
  };

  /** `asDraft` saves a new assignment without making visits, for a job not yet finalised. */
  async function save(asDraft: boolean) {
    setBusy(true);
    setProblems([]);
    setFields({});
    const payload = payloadOf();
    if (asDraft) payload.draft = true;
    try {
      if (editing) await put(`/api/assignments/${props.id}`, payload);
      else await post('/api/assignments', payload);
      nav(asDraft ? '/assignments?status=draft' : '/assignments?status=all');
    } catch (err) {
      setReview(null);
      fail(err);
      setBusy(false);
    }
  }

  /** Save, after a look at what it will create: the visits, and what they earn, cost and leave. */
  async function submit(e: FormEvent) {
    e.preventDefault();
    // A draft makes no visits, so there is nothing to confirm.
    if (isDraft) return void save(false);
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      const p = await post<Preview>('/api/assignments/preview', payloadOf());
      setBusy(false);
      // Invalid input: saving reports each problem against its field, as before.
      if (Object.keys(p.errors ?? {}).length > 0 || !Array.isArray(p.visits))
        return void save(false);
      setReview(p);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  const baseline: Form = { ...props.initial, notes: savedNotes };
  const dirty = !busy && JSON.stringify(f) !== JSON.stringify(baseline);
  const futureVisits = props.loaded?.future_visits ?? 0;
  const visitsText = `${futureVisits} ${futureVisits === 1 ? 'visit' : 'visits'}`;

  async function move(status: string, needsNote: boolean) {
    // A status change doesn't save edits on the page, so it's only offered once they're saved or undone.
    if (dirty) return;
    let note = '';
    if (status === 'completed') {
      const yes = await askConfirm({
        title: 'Mark this assignment completed?',
        body:
          futureVisits > 0
            ? `No new visits will be made. The ${visitsText} already booked from today stay on the calendar. This can't be undone.`
            : "No new visits will be made. This can't be undone.",
        confirmLabel: 'Mark completed',
        danger: true,
      });
      if (!yes) return;
    }
    if (needsNote) {
      note =
        (await askText({
          title:
            status === 'cancelled' ? 'Cancel this assignment?' : 'Put this assignment on hold?',
          body:
            status === 'cancelled'
              ? `${futureVisits > 0 ? `Its ${visitsText} from today come off the calendar. ` : ''}This can't be undone. Say why, for whoever looks at it next.`
              : 'No visits are made while it is on hold. Say what it is waiting for.',
          label: status === 'cancelled' ? 'Reason' : 'Waiting for',
          required: true,
          confirmLabel: status === 'cancelled' ? 'Cancel assignment' : 'Put on hold',
          danger: status === 'cancelled',
        })) ?? '';
      if (note.trim() === '') return;
    }
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      // A draft's latest details are saved first, so scheduling checks what is on screen.
      if (isDraft) await put(`/api/assignments/${props.id}`, payloadOf());
      await post(`/api/assignments/${props.id}/status`, { status, note });
      nav('/assignments');
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  const flat = f.charge_rate_unit === 'flat';
  const monthly = f.pattern === 'monthly';
  const manual = f.pattern === 'manual';
  const multiWeek = f.pattern === 'fortnightly' || f.pattern === 'triweekly';
  const cycleWeeks = f.pattern === 'fortnightly' ? 2 : 3;
  const m = marginOf(f);
  const before = marginOf(props.initial);
  const custErr = (k: string) => fields[`customer_${k}`] || fields[`new_customer_${k}`];
  const cd = f.customer_details;
  const customerName =
    props.loaded?.customer_name || [cd.first_name, cd.last_name].filter(Boolean).join(' ');

  const status = props.loaded?.assignment_status ?? '';
  const statusLabel = props.ended ? 'Ended' : (STATUS_LABELS[status] ?? status);

  // ---- Sections ----

  const closeNotes = () => {
    setEditingNotes(false);
    setNotesError('');
    requestAnimationFrame(() => notesEditRef.current?.focus());
  };
  const startNotes = () => {
    setNotesDraft(f.notes ?? '');
    setNotesError('');
    setEditingNotes(true);
  };
  /** Done saves the notes straight away; nothing else on the page is saved with them. */
  async function saveNotes() {
    setNotesBusy(true);
    setNotesError('');
    try {
      const r = await put<{ notes: string }>(`/api/assignments/${props.id}/notes`, {
        notes: notesDraft,
      });
      set('notes', r.notes);
      setSavedNotes(r.notes);
      closeNotes();
      toast(r.notes ? 'Office notes saved.' : 'Office notes removed.');
    } catch (err) {
      setNotesError(err instanceof ApiError ? err.message : 'Could not reach the server');
    } finally {
      setNotesBusy(false);
    }
  }
  // Only once there are notes to show: they are first written in Extras.
  const notesCallout = editing && ((f.notes ?? '').trim() !== '' || editingNotes) && (
    <section className="notes-callout" aria-labelledby="notes-callout-h">
      <div className="notes-callout-head">
        <h2 id="notes-callout-h">Office notes</h2>
        {!editingNotes && (
          <button
            ref={notesEditRef}
            type="button"
            className="quiet icon-button"
            onClick={startNotes}
          >
            <EditIcon />
            <span className="sr-only">Edit office notes</span>
          </button>
        )}
      </div>
      {editingNotes ? (
        <>
          <textarea
            ref={notesRef}
            aria-label="Edit office notes"
            value={notesDraft}
            disabled={notesBusy}
            onChange={(e) => setNotesDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeNotes();
              }
            }}
          />
          {notesError && (
            <p role="alert" className="error">
              Not saved: {notesError}
            </p>
          )}
          <div className="notes-callout-actions">
            <button type="button" disabled={notesBusy} onClick={() => void saveNotes()}>
              {notesBusy ? 'Saving…' : 'Done'}
            </button>
            <button type="button" className="quiet" disabled={notesBusy} onClick={closeNotes}>
              Cancel<span className="sr-only"> editing notes</span>
            </button>
            <span className="muted notes-callout-hint">Saved straight away.</span>
          </div>
        </>
      ) : (
        <p className="notes-callout-text">{f.notes}</p>
      )}
    </section>
  );

  const effectiveSection = live && !props.ended && (
    <section className="panel edit-from" aria-labelledby="edit-from-h">
      <h2 id="edit-from-h" className="sr-only">
        When these changes apply
      </h2>
      <Row
        label="Changes apply from"
        error={fields.effective_date}
        hint="Visits before this date stay as they are booked."
      >
        <input
          type="date"
          aria-label="Changes apply from"
          min={todayLocal()}
          value={f.effective_date}
          onChange={(e) => set('effective_date', e.target.value)}
        />
      </Row>
    </section>
  );

  const summarySection = editing && props.loaded && (
    <dl className="arrangement" aria-label="As it stands">
      <div>
        <dt>Schedule</dt>
        <dd>{patternText(props.initial)}</dd>
      </div>
      <div>
        <dt>Lead</dt>
        <dd>
          {props.loaded.lead_name ||
            (props.initial.open_to_claim ? 'Offered to contractors' : 'Unassigned')}
        </dd>
      </div>
      <div>
        <dt>Charge / pay</dt>
        <dd>
          {rateText(props.initial.charge_rate, props.initial.charge_rate_unit)} /{' '}
          {props.initial.lead_id > 0
            ? rateText(props.initial.lead_pay_rate, props.initial.lead_pay_rate_unit)
            : props.initial.open_to_claim
              ? rateText(props.initial.offer_pay_rate, props.initial.offer_pay_rate_unit)
              : '—'}
        </dd>
      </div>
      {before.known && (
        <div>
          <dt>Margin</dt>
          <dd className={before.margin < 0 ? 'neg' : ''}>
            {formatMoney(before.margin / 100)} a visit
          </dd>
        </div>
      )}
    </dl>
  );

  const customerFields = (
    <>
      <Row label="Name" group error={custErr('last_name') || custErr('first_name')}>
        <div className="inline field-pair">
          <input
            aria-label="First name"
            placeholder="First name"
            value={cd.first_name}
            onChange={(e) => setCust({ first_name: e.target.value })}
          />
          <input
            aria-label="Last name"
            placeholder="Last name"
            value={cd.last_name}
            onChange={(e) => setCust({ last_name: e.target.value })}
          />
        </div>
      </Row>
      <Row label="Contact" group error={custErr('email') || custErr('mobile') || custErr('phone')}>
        <div className="inline field-pair">
          <input
            type="email"
            aria-label="Email"
            placeholder="Email"
            value={cd.email}
            onChange={(e) => setCust({ email: e.target.value })}
          />
          <input
            type="tel"
            aria-label="Phone"
            placeholder="Phone"
            value={cd.phone}
            onChange={(e) => setCust({ phone: phoneField(e.target.value) })}
          />
        </div>
      </Row>
      <Row
        label="Address"
        group
        error={fields.service_address_line1 || custErr('address') || custErr('line1')}
        wide
      >
        <div className="address-fields">
          <input
            aria-label="Street address"
            placeholder="Street address"
            value={cd.address.line1}
            onChange={(e) => setCust({ address: { ...cd.address, line1: e.target.value } })}
          />
          <input
            aria-label="Suburb"
            placeholder="Suburb"
            value={cd.address.locality}
            onChange={(e) => setCust({ address: { ...cd.address, locality: e.target.value } })}
          />
          <input
            aria-label="Postcode"
            placeholder="Postcode"
            className="short"
            value={cd.address.postcode}
            onChange={(e) => setCust({ address: { ...cd.address, postcode: e.target.value } })}
          />
        </div>
      </Row>
      <Row label="Access notes" hint="How the cleaner gets in: keys, codes, pets." wide>
        <textarea
          aria-label="Access notes"
          value={cd.access_notes}
          onChange={(e) => setCust({ access_notes: e.target.value })}
        />
      </Row>
    </>
  );

  const customerSection = (
    <section key="customer" aria-labelledby="customer-h">
      <h2 id="customer-h">Customer</h2>
      <div className="panel">
        {customerMode === 'new' ? (
          <>
            <p className="panel-lede">
              A new customer, saved with these details.{' '}
              <button type="button" className="link" onClick={() => setCustomerMode('existing')}>
                Pick an existing customer instead
              </button>
            </p>
            {customerFields}
          </>
        ) : (
          <>
            {picking || f.customer_id === 0 ? (
              <Row label="Customer" error={fields.customer_id} hint="Where the work happens.">
                <ContactPicker
                  role="customer"
                  label="Customer"
                  value={f.customer_id}
                  onChange={(v) => void pickCustomer(v)}
                />
              </Row>
            ) : (
              <p className="panel-lede">
                {personCustomer
                  ? `Changes here are saved to ${customerName || 'the customer'}'s record, so their other jobs get them too.`
                  : `${customerName} is an organisation; edit its details on its own page.`}{' '}
                <Link to={`/people/${f.customer_id}`}>Open their record</Link>
                {' · '}
                <button type="button" className="link" onClick={() => setPicking(true)}>
                  Use a different customer
                </button>
              </p>
            )}
            {!editing && f.customer_id === 0 && (
              <p className="panel-lede">
                <button type="button" className="link" onClick={() => setCustomerMode('new')}>
                  Add a new customer instead
                </button>
              </p>
            )}
            {f.customer_id > 0 && personCustomer && customerFields}
            <details className="more" open={f.own_address || f.own_access_notes}>
              <summary>Different address or access for this job only</summary>
              <label className="check">
                <input
                  type="checkbox"
                  checked={f.own_address}
                  onChange={(e) => set('own_address', e.target.checked)}
                />
                This job is at a different address
              </label>
              {f.own_address &&
                (['line1', 'locality', 'postcode'] as const).map((k) => (
                  <Field
                    key={k}
                    label={{ line1: 'Address', locality: 'Suburb', postcode: 'Postcode' }[k]}
                    error={fields[`service_address_${k}`]}
                  >
                    <input
                      value={f.service_address[k]}
                      onChange={(e) =>
                        set('service_address', { ...f.service_address, [k]: e.target.value })
                      }
                    />
                  </Field>
                ))}
              <label className="check">
                <input
                  type="checkbox"
                  checked={f.own_access_notes}
                  onChange={(e) => set('own_access_notes', e.target.checked)}
                />
                This job has its own access notes
              </label>
              {f.own_access_notes && (
                <Field label="Access notes for this job (blank means none)">
                  <textarea
                    value={f.access_notes}
                    onChange={(e) => set('access_notes', e.target.value)}
                  />
                </Field>
              )}
            </details>
          </>
        )}
      </div>
    </section>
  );

  const billingSection = (
    <section key="billing" aria-labelledby="billing-h">
      <h2 id="billing-h">Billing</h2>
      <div className="panel">
        <Row
          label="Client / Insurer"
          error={fields.client_id}
          hint="Invoices go to them, not to the customer."
        >
          <ContactPicker
            role="client"
            label="Client / Insurer"
            value={f.client_id}
            onChange={(v) => set('client_id', v)}
          />
        </Row>
        <Row label="OT / rehab company" error={fields.referrer_id}>
          <ContactPicker
            role="referrer"
            label="OT / rehab company"
            value={f.referrer_id}
            onChange={(v) => set('referrer_id', v)}
          />
        </Row>
        <Row label="Invoice description" error={fields.invoice_description} wide>
          <input
            aria-label="Invoice description"
            value={f.invoice_description}
            onChange={(e) => set('invoice_description', e.target.value)}
          />
        </Row>
        <Row
          label="Claim number"
          error={fields.client_reference}
          hint="Used to match invoices to the customer's claim."
        >
          <input
            aria-label="Claim number"
            value={f.client_reference}
            onChange={(e) => set('client_reference', e.target.value)}
          />
        </Row>
      </div>
    </section>
  );

  const teamSection = (
    <section key="team" aria-labelledby="team-h">
      <h2 id="team-h">Team</h2>
      <div className="panel">
        {/* Only worth asking when the business has more than one type of work. */}
        {(jobTypes.length > 1 || f.job_types.length > 0) && (
          <Row
            label="Type of work"
            hint="Only contractors who do one of these types of work are offered below."
          >
            <JobTypes
              options={jobTypes}
              value={f.job_types}
              onChange={(v) => set('job_types', v)}
            />
          </Row>
        )}
        <Row label="Lead contractor" error={fields.lead_id}>
          <ContactPicker
            role="contractor"
            label="Lead contractor"
            contractorTypes={f.job_types}
            value={f.lead_id}
            card
            onChange={(v) => set('lead_id', v)}
          />
        </Row>
        {f.team.length > 0 && (
          <Row label="Also on the team" group error={fields.team} wide>
            {f.team.map((mem, i) => (
              <div key={i} className="team-member">
                <ContactPicker
                  role="contractor"
                  label={`Team member ${i + 1}`}
                  contractorTypes={f.job_types}
                  value={mem.contact_id}
                  exclude={[f.lead_id, ...f.team.map((t) => t.contact_id)]}
                  onChange={(v) => setMember(i, { contact_id: v })}
                />
                <Rate
                  name={`Team member ${i + 1} pay`}
                  unitLabel={`Team member ${i + 1} pay type`}
                  unit={mem.pay_rate_unit}
                  value={mem.pay_rate}
                  onUnit={(v) => setMember(i, { pay_rate_unit: v })}
                  onValue={(v) => setMember(i, { pay_rate: v })}
                />
                <button
                  type="button"
                  className="quiet"
                  onClick={() =>
                    set(
                      'team',
                      f.team.filter((_, n) => n !== i),
                    )
                  }
                >
                  Remove<span className="sr-only"> team member {i + 1}</span>
                </button>
              </div>
            ))}
          </Row>
        )}
        {f.team.length === 0 && fields.team && <span className="error">{fields.team}</span>}
        <button
          type="button"
          className="quiet add-tag"
          onClick={() =>
            set('team', [...f.team, { contact_id: 0, pay_rate: '', pay_rate_unit: 'hour' }])
          }
        >
          + Add team member
        </button>
        {f.lead_id === 0 && (
          <label className="check">
            <input
              type="checkbox"
              checked={f.open_to_claim}
              onChange={(e) => set('open_to_claim', e.target.checked)}
            />
            Offer it to contractors to claim
          </label>
        )}
      </div>
    </section>
  );

  const ratesSection = (
    <section key="rates" aria-labelledby="rates-h">
      <h2 id="rates-h">Rates</h2>
      <div className="panel">
        <Row label="Charge client" group error={fields.charge_rate || fields.charge_rate_unit} wide>
          <Rate
            name="Amount charged"
            unitLabel="Charge type"
            unit={f.charge_rate_unit}
            value={f.charge_rate}
            onUnit={(v) => set('charge_rate_unit', v)}
            onValue={(v) => set('charge_rate', v)}
          />
        </Row>
        {f.lead_id > 0 || !f.open_to_claim ? (
          <Row
            label="Pay lead contractor"
            group
            error={fields.lead_pay_rate}
            wide
            hint={
              f.lead_id === 0 ? 'Choose a lead contractor in Team to set their pay.' : undefined
            }
          >
            <Rate
              name="Contractor pay"
              unitLabel="Pay type"
              unit={f.lead_pay_rate_unit}
              value={f.lead_pay_rate}
              disabled={f.lead_id === 0}
              onUnit={(v) => set('lead_pay_rate_unit', v)}
              onValue={(v) => set('lead_pay_rate', v)}
            />
          </Row>
        ) : (
          <Row
            label="Offered pay"
            group
            error={fields.offer_pay_rate}
            wide
            hint="What whoever claims the job is paid."
          >
            <Rate
              name="Offered pay"
              unitLabel="Offered pay type"
              unit={f.offer_pay_rate_unit}
              value={f.offer_pay_rate}
              onUnit={(v) => set('offer_pay_rate_unit', v)}
              onValue={(v) => set('offer_pay_rate', v)}
            />
          </Row>
        )}
        {f.team.length > 0 && (
          <p className="panel-lede muted">
            Team members&apos; pay is set next to each of them in Team, and counted below.
          </p>
        )}
        <Row
          label="Margin"
          hint="On a typical visit, before public holidays and one-off changes. The review shows each visit."
        >
          <output className={m.margin < 0 ? 'margin neg' : 'margin'} aria-live="polite">
            {m.known
              ? `${formatMoney(m.margin / 100)} a visit${m.charge > 0 ? ` (${Math.round((m.margin / m.charge) * 100)}%)` : ''}`
              : 'Set the charge and visit times to see it'}
          </output>
        </Row>
      </div>
    </section>
  );

  const slotLabel = (i: number, what: string) =>
    f.slots.length > 1 ? `Visit ${i + 1} ${what}` : what;

  const scheduleSection = (
    <section key="schedule" aria-labelledby="schedule-h">
      <h2 id="schedule-h" className="sr-only">
        Schedule
      </h2>
      <div className="panel">
        <Row label="Repeats" error={fields.pattern}>
          <select
            aria-label="Repeats"
            value={f.pattern}
            onChange={(e) => set('pattern', e.target.value)}
          >
            {PATTERNS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Row>
        {!manual && (
          <Row
            label="Dates"
            group
            error={fields.start_date || fields.end_date || fields.visit_count}
            wide
            hint={flat ? undefined : 'Leave the end blank to keep going.'}
          >
            <div className="inline">
              <label className="inline-label">
                Starts
                <input
                  type="date"
                  value={f.start_date}
                  onChange={(e) => set('start_date', e.target.value)}
                />
              </label>
              {flat ? (
                <label className="inline-label">
                  for
                  <input
                    inputMode="numeric"
                    aria-label="Number of visits"
                    className="short num"
                    value={f.visit_count || ''}
                    onChange={(e) => set('visit_count', Number(e.target.value) || 0)}
                  />
                  visits
                </label>
              ) : (
                <label className="inline-label">
                  Ends
                  <input
                    type="date"
                    value={f.end_date}
                    onChange={(e) => set('end_date', e.target.value)}
                  />
                </label>
              )}
            </div>
          </Row>
        )}
        <Row label={manual ? 'Dates' : 'Visits'} group error={fields.slots} wide>
          {f.slots.map((s, i) => (
            <div key={i} className="inline slot-row">
              {manual && (
                <input
                  type="date"
                  aria-label={slotLabel(i, 'date')}
                  value={s.date}
                  onChange={(e) => setSlot(i, { date: e.target.value })}
                />
              )}
              {monthly && (
                <select
                  aria-label={slotLabel(i, 'which week of the month')}
                  value={s.ordinal || '1st'}
                  onChange={(e) => setSlot(i, { ordinal: e.target.value })}
                >
                  {ORDINALS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}
              {multiWeek && (
                <select
                  aria-label={slotLabel(i, 'week of cycle')}
                  value={s.week}
                  onChange={(e) => setSlot(i, { week: Number(e.target.value) })}
                >
                  {Array.from({ length: cycleWeeks }, (_, n) => (
                    <option key={n} value={n + 1}>
                      Week {n + 1}
                    </option>
                  ))}
                </select>
              )}
              {!manual && (
                <select
                  aria-label={slotLabel(i, 'day')}
                  value={s.weekday}
                  onChange={(e) => setSlot(i, { weekday: Number(e.target.value) })}
                >
                  {DAYS.map((d, n) => (
                    <option key={d} value={n + 1}>
                      {d}
                    </option>
                  ))}
                </select>
              )}
              <span className="time-range">
                <input
                  type="time"
                  aria-label={slotLabel(i, 'starts')}
                  value={s.start_time}
                  onChange={(e) => setSlot(i, { start_time: e.target.value })}
                />
                <span aria-hidden="true">to</span>
                <input
                  type="time"
                  aria-label={slotLabel(i, 'ends')}
                  value={s.end_time}
                  onChange={(e) => setSlot(i, { end_time: e.target.value })}
                />
              </span>
              {f.slots.length > 1 && (
                <button
                  type="button"
                  className="quiet"
                  onClick={() =>
                    set(
                      'slots',
                      f.slots.filter((_, n) => n !== i),
                    )
                  }
                >
                  Remove<span className="sr-only"> visit {i + 1}</span>
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="quiet add-tag"
            onClick={() =>
              set('slots', [
                ...f.slots,
                {
                  weekday: 1,
                  ordinal: monthly ? '1st' : '',
                  week: 1,
                  date: '',
                  start_time: '09:00',
                  end_time: '11:00',
                },
              ])
            }
          >
            {manual ? '+ Add date' : '+ Add day'}
          </button>
        </Row>
      </div>
    </section>
  );

  const extrasSection = (
    <section key="extras" aria-label="Extras">
      <div className="panel">
        <Row label="Checklist" error={fields.checklist_template_id} wide>
          <ChecklistPicker
            label="Checklist"
            value={f.checklist_template_id}
            jobTypes={f.job_types}
            onChange={(v) => set('checklist_template_id', v)}
          />
        </Row>
        <Row label="Tags" wide>
          <TagPicker label="Tags" value={f.tag_ids} onChange={(v) => set('tag_ids', v)} />
        </Row>
        <Row label="Office notes" wide>
          <textarea
            aria-label="Office notes"
            value={f.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </Row>
      </div>
    </section>
  );

  const panels: Record<Tab, ReactNode> = {
    customer: [customerSection, billingSection],
    team: [teamSection, ratesSection],
    schedule: [
      scheduleSection,
      props.id && !props.ended && <SkipDates key="skips" assignmentId={props.id} />,
    ],
    extras: extrasSection,
    history: props.id && <HistoryTable table="assignments" id={props.id} />,
  };
  const flaggedTab = (t: Tab) => Object.keys(fields).some((k) => tabOf(k) === t);

  /** Show a tab and put focus on it, bringing the tabs back into view on a long page. */
  const go = (t: Tab) => {
    setTab(t);
    const el = tabRefs.current[t];
    el?.focus();
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' });
  };
  const onTabKey = (e: KeyboardEvent) => {
    const at = order.indexOf(tab);
    const keys: Record<string, number> = {
      ArrowRight: at + 1,
      ArrowLeft: at - 1,
      Home: 0,
      End: order.length - 1,
    };
    const next = keys[e.key];
    if (next === undefined) return;
    e.preventDefault();
    go(order[(next + order.length) % order.length]!);
  };

  const tabs = (
    <div
      role="tablist"
      aria-label="Assignment details"
      className={stepped ? 'form-tabs stepped' : 'form-tabs'}
      onKeyDown={onTabKey}
    >
      {order.map((t, i) => {
        const { label, short, Icon } = TABS[t];
        const on = tab === t;
        return (
          <button
            key={t}
            ref={(el) => {
              tabRefs.current[t] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${t}`}
            aria-selected={on}
            aria-controls={`panel-${t}`}
            tabIndex={on ? 0 : -1}
            className={on ? 'on' : undefined}
            onClick={() => setTab(t)}
          >
            <span className="form-tab-icon">
              <Icon />
            </span>
            {stepped && i < steps.length && <span className="sr-only">Step {i + 1}: </span>}
            <span className="form-tab-label">{label}</span>
            <span className="form-tab-short" aria-hidden="true">
              {short}
            </span>
            {flaggedTab(t) && (
              <>
                <span className="form-tab-flag" aria-hidden="true" />
                <span className="sr-only"> (has a problem)</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  const tabPanels = order.map((t, i) => (
    <div
      key={t}
      role="tabpanel"
      id={`panel-${t}`}
      aria-labelledby={`tab-${t}`}
      className="form-tab-panel stack"
      hidden={tab !== t}
    >
      {panels[t]}
      {stepped && i < steps.length && (
        <div className="step-nav">
          {i > 0 && (
            <button type="button" className="quiet" onClick={() => go(steps[i - 1]!)}>
              Back
            </button>
          )}
          <span className="muted step-count">
            Step {i + 1} of {steps.length}
          </span>
          {i < steps.length - 1 && (
            <button type="button" className="quiet next" onClick={() => go(steps[i + 1]!)}>
              Next: {TABS[steps[i + 1]!].label}
            </button>
          )}
        </div>
      )}
    </div>
  ));

  const moves = props.loaded?.next_statuses ?? [];

  return (
    <form onSubmit={submit} className="stack assignment-form" noValidate>
      <UnsavedChanges when={dirty} />
      <div className={props.loaded ? 'assignment-head assignment-card' : 'assignment-head'}>
        <div className="assignment-title">
          {props.loaded && (
            <div className="assignment-eyebrow">
              {props.loaded.number ? (
                <span className="record-number">{assignmentRef(props.loaded.number)}</span>
              ) : null}
              <span className={`pill ${props.ended ? '' : (STATUS_TONE[status] ?? 'info')}`}>
                {statusLabel}
              </span>
            </div>
          )}
          <h1>
            {editing
              ? props.loaded?.customer_name || 'Assignment'
              : props.copy
                ? `Copy of ${props.copy.name || 'assignment'}`
                : 'Add assignment'}
          </h1>
          {editing && props.loaded?.client_name && (
            <p className="muted">Billed to {props.loaded.client_name}</p>
          )}
        </div>
        {props.loaded && (
          <div className="assignment-status">
            <Link className="button quiet-button" to={`/assignments/new?from=${props.id}`}>
              Duplicate
            </Link>
            {moves.length > 0 && (
              <details className="status-menu">
                <summary className="button quiet-button">Change status</summary>
                <div className="status-menu-list">
                  {dirty && (
                    <p className="muted">
                      Save or undo your changes first; a status change doesn&apos;t save them.
                    </p>
                  )}
                  {moves.map((n) => (
                    <button
                      key={n.status}
                      type="button"
                      className={n.status === 'cancelled' ? 'quiet danger-text' : 'quiet'}
                      disabled={dirty || busy}
                      onClick={() => void move(n.status, n.needs_note)}
                    >
                      {MOVE_LABEL[n.status] ?? n.label}
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        {props.loaded?.status_note && (
          <p className="muted status-note">{props.loaded.status_note}</p>
        )}
        {summarySection}
      </div>
      {props.copy && (
        <p className="notice">
          A copy, not yet saved. Choose its start date on the Schedule tab; its visits are made
          only when you create it.
        </p>
      )}
      {props.ended && (
        <p className="notice">
          This assignment ended on {dayLabel(props.loaded!.end_date!)}. It is marked completed once
          its last visits are closed. To keep it going, change the end date below.
        </p>
      )}
      {problems.length > 0 && (
        <div role="alert" className="error box" tabIndex={-1} ref={problemsRef}>
          <strong>Not saved.</strong>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {notesCallout}
      {effectiveSection}
      {tabs}
      {tabPanels}

      {review && (
        <AssignmentReview
          preview={review}
          editing={editing}
          changes={
            live
              ? changesOf(baseline, f, {
                  before: props.loaded?.lead_name ?? '',
                  after: review.summary?.lead_name ?? '',
                })
              : undefined
          }
          busy={busy}
          onCancel={() => setReview(null)}
          onConfirm={() => void save(false)}
        />
      )}

      <div className="form-actions">
        <button type="submit" disabled={busy}>
          {busy
            ? 'Saving…'
            : isDraft
              ? 'Save draft'
              : editing
                ? 'Save changes'
                : 'Create assignment and visits'}
        </button>
        {!editing && (
          <button type="button" className="quiet" disabled={busy} onClick={() => save(true)}>
            Save as draft
          </button>
        )}
        <button type="button" className="quiet" onClick={() => nav('/assignments')}>
          Cancel
        </button>
        {dirty && <span className="muted">Unsaved changes</span>}
      </div>
    </form>
  );
}
